/**
 * PENDING INVITATIONS — the list nobody could see.
 *
 * The backend has exposed `GET /dashboard/invitations` (with status, expiry and
 * resend count), `POST /dashboard/invitations/:id/resend` and
 * `POST /dashboard/invitations/:id/revoke` all along, and the audit found ZERO
 * callers for any of them. So an admin who invited somebody had no way to see
 * whether the invitation had been opened, no way to resend one that had expired
 * — invites expire after seven days — and no way to revoke one sent to the
 * wrong address. The only signal that anything had happened was the person
 * eventually appearing in the Users list, or not.
 *
 * "Not" is the common case worth designing for: an invite that silently expires
 * looks exactly like a colleague ignoring you.
 *
 * Placed on the Users screen rather than behind its own route because an
 * invitation is a person who is not in the list yet, and the question "where is
 * Sam" is asked in front of the list Sam is missing from.
 */
import React from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { apiRoute } from '../../../../constants/api-constants';
import toast from '../../../../utils/toast';
import { AppButton } from '../../../button/app-button';
import { useResource } from '../../../hooks/use-resource';
import { LoadError } from '../dashboard-shared-components/load-state';
import { down } from '../../../../style-utils/media-queries';
import {
  palette,
  radius,
  spacing,
  status,
  typography,
} from '../../../../style-utils/theme';

export interface PendingInvitation {
  id: string;
  role: string;
  email: string | null;
  first_name?: string | null;
  last_name?: string | null;
  status: string;
  expires_at: string | null;
  resend_count: number;
  created_at: string;
}

const Wrap = styled.section`
  margin: ${spacing.sm} 0;
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
`;

const Head = styled.h2`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Lead = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.xs} 0;
  border-top: 1px solid ${palette.line};

  ${down('sm')} {
    grid-template-columns: 1fr auto;
  }
`;

const Who = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-family: ${typography.ui};
    font-size: ${typography.size.sm}px;
    color: ${palette.ink};
    overflow-wrap: anywhere;
  }

  span {
    font-family: ${typography.ui};
    font-size: ${typography.size.xxs}px;
    color: ${palette.slate};
  }
`;

const State = styled.span<{ $tone: 'warning' | 'neutral' | 'alert' }>`
  padding: ${spacing.half} ${spacing.half};
  border-radius: ${radius.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  background: ${({ $tone }) => status[$tone].bg};
  color: ${({ $tone }) => status[$tone].fg};
  white-space: nowrap;
`;

/** Expired is not "pending"; it is a thing the admin has to act on. */
const describe = (
  invitation: PendingInvitation
): { label: string; tone: 'warning' | 'neutral' | 'alert' } => {
  const expiresAt = invitation.expires_at
    ? new Date(invitation.expires_at)
    : null;

  if (expiresAt && expiresAt.getTime() < Date.now()) {
    return { label: 'Expired', tone: 'alert' };
  }

  if (!expiresAt) return { label: 'Sent', tone: 'neutral' };

  const days = Math.max(
    Math.ceil((expiresAt.getTime() - Date.now()) / 86400000),
    0
  );

  return {
    label: days <= 1 ? 'Expires today' : `Expires in ${days} days`,
    tone: days <= 2 ? 'warning' : 'neutral',
  };
};

export const PendingInvitations: React.FC = () => {
  const invitations = useResource<PendingInvitation[]>(
    async () => {
      const response = await axios.get(`${apiRoute}/dashboard/invitations`);

      return Array.isArray(response.data) ? response.data : [];
    },
    { fallbackMessage: 'We could not load your pending invitations.' }
  );

  const rows = (invitations.data || []).filter(
    invitation => invitation.status === 'pending'
  );

  const resend = async (invitation: PendingInvitation) => {
    try {
      await axios.post(
        `${apiRoute}/dashboard/invitations/${invitation.id}/resend`
      );
      toast.success(`Invitation resent to ${invitation.email || 'them'}.`);
      await invitations.reload();
    } catch {
      toast.error('We could not resend that invitation.');
    }
  };

  const revoke = async (invitation: PendingInvitation) => {
    try {
      await axios.post(
        `${apiRoute}/dashboard/invitations/${invitation.id}/revoke`
      );
      toast.success('Invitation revoked. The link no longer works.');
      await invitations.reload();
    } catch {
      toast.error('We could not revoke that invitation.');
    }
  };

  // Silent when there is nothing outstanding: a permanently visible empty panel
  // above the people list is noise on every visit for a state that is normal.
  if (invitations.isLoading) return null;
  if (!invitations.error && rows.length === 0) return null;

  return (
    <Wrap>
      <Head>Invitations waiting</Head>
      <Lead>
        People who have been invited and have not set up their account yet.
        Invitations expire after seven days.
      </Lead>

      {invitations.error ? (
        <LoadError message={invitations.error} onRetry={invitations.reload} />
      ) : (
        rows.map(invitation => {
          const state = describe(invitation);

          return (
            <Row key={invitation.id}>
              <Who>
                <strong>
                  {[invitation.first_name, invitation.last_name]
                    .filter(Boolean)
                    .join(' ') ||
                    invitation.email ||
                    'Invited person'}
                </strong>
                <span>
                  {invitation.email}
                  {invitation.resend_count > 0
                    ? ` · resent ${invitation.resend_count}×`
                    : ''}
                </span>
              </Who>
              <State $tone={state.tone}>{state.label}</State>
              <AppButton
                variant="secondary"
                size="compact"
                onClick={() => resend(invitation)}
              >
                Resend
              </AppButton>
              <AppButton
                variant="ghost"
                size="compact"
                onClick={() => revoke(invitation)}
              >
                Revoke
              </AppButton>
            </Row>
          );
        })
      )}
    </Wrap>
  );
};
